// Ultrafast is a requested service tier, not a model SKU, reasoning preset,
// alias for Fast/Priority. These rates are display metadata, not billing logic.
export const ULTRAFAST_SOURCE_URL = 'https://developers.openai.com/api/docs/guides/ultrafast-mode';
export const ULTRAFAST_CODEX_SOURCE_URL = 'https://learn.chatgpt.com/docs/agent-configuration/speed#ultrafast-mode';
export const ULTRAFAST_SERVICE_TIER_HELP = 'Ultrafast is available here only for GPT-6.1 Sol and is distinct from Fast and Priority. API pricing is 6× Standard; availability and billing are server-authoritative.';
export const GPT61_SOL_ULTRAFAST_PRICING = {
  standardMultiplier: 6,
  regionalProcessingMultiplier: 1.1,
  inputPricePerMillion: 12,
  cachedInputPricePerMillion: 0.6,
  cacheWritePricePerMillion: 15,
  outputPricePerMillion: 60,
  longContextPricing: {
    threshold: 272000,
    inputPricePerMillion: 24,
    cachedInputPricePerMillion: 1.2,
    cacheWritePricePerMillion: 30,
    outputPricePerMillion: 90,
  },
};
export const ULTRAFAST_API_WARNING = 'Higher-cost mode: Ultrafast API pricing is 6× Standard. Per 1M tokens at up to 272k input tokens: $12 input / $0.60 cached input / $15 cache writes / $60 output. Above 272k input tokens, the entire request uses $24 / $1.20 / $30 / $90 respectively. Regional processing adds 10% where available. Separate Ultrafast rate limits apply; requesting this tier does not guarantee it will be served. API routes do not require a ChatGPT/Codex plan entitlement.';
export const ULTRAFAST_CODEX_WARNING = 'Codex Ultrafast is plan- and workspace-gated: Pro $500 and eligible Enterprise/Edu workspaces. Admin enablement is required. Included usage is charged at 8×; purchased credit usage at 6×. The CLI/provider determines eligibility; selecting Ultrafast does not unlock access. These Codex requirements do not gate API routes.';

export const normalizeServiceTier = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() || null : null
);

export const supportsUltrafastServiceTier = (model) => (
  ['gpt-6.1-sol', 'openai:gpt-6.1-sol'].includes(String(model || '').trim().toLowerCase())
);

export const normalizeServiceTierForModel = (model, value) => {
  const tier = normalizeServiceTier(value);
  if (String(model || '').trim().toLowerCase().split(':').pop().trim() === 'gemini-4-argon') return ['standard', 'flex', 'priority'].includes(tier) ? tier : null;
  if (String(model || '').trim().toLowerCase().split(':').pop() === 'muse-wonder-1.0') {
    return tier === 'standard' ? tier : null;
  }
  return tier === 'ultrafast' && !supportsUltrafastServiceTier(model) ? null : tier;
};

export const getServiceTierOptions = (model) => String(model || '').trim().toLowerCase().split(':').pop() === 'muse-wonder-1.0' ? [
  { value: null, label: 'Default' },
  { value: 'standard', label: 'Standard' },
] : [
  { value: null, label: 'Default' },
  { value: 'standard', label: 'Standard' },
  { value: 'flex', label: 'Flex' },
  { value: 'priority', label: 'Priority' },
  ...(supportsUltrafastServiceTier(model) ? [{ value: 'ultrafast', label: 'Ultrafast' }] : []),
];

// Preserve existing tiers (including legacy Fast) and fail before network
// dispatch if a stale/programmatic request tries Ultrafast on another model.
export const resolveServiceTierRequest = (payload = {}) => {
  if (!Object.prototype.hasOwnProperty.call(payload, 'service_tier') || payload.service_tier === undefined) return payload;
  const tier = normalizeServiceTier(payload.service_tier);
  if (tier === 'ultrafast' && !supportsUltrafastServiceTier(payload.model)) {
    throw new Error('Ultrafast is available only for GPT-6.1 Sol.');
  }
  return { ...payload, service_tier: tier };
};

export const formatServiceTier = (value) => {
  const tier = normalizeServiceTier(value);
  return tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '';
};
